import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Link2, CheckCircle, LogOut } from 'lucide-react';
import { apiClient } from '../api/client';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';

const NoOrgState = () => {
  const navigate = useNavigate();
  const { user, switchOrg, logout } = useAuth();

  const [orgName, setOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [inviteInput, setInviteInput] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [joinSuccess, setJoinSuccess] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreating(true);
    try {
      const res = await apiClient.post('/api/organizations', { name: orgName });
      const tenantId = res.data?.id || res.data?.tenant_id;
      if (tenantId) {
        await switchOrg(tenantId);
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Failed to create organisation.');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setJoinError(null);
    setJoining(true);
    try {
      let token = inviteInput.trim();
      try {
        const parsed = new URL(token);
        token = parsed.searchParams.get('token') || token;
      } catch {
        // not a full URL — treat as raw token
      }
      await orgApi.acceptInvitation(token);
      setJoinSuccess(true);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setJoinError(err.response?.data?.detail || 'Invalid or expired invitation link.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-dark-900 p-8 relative">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-8 left-8 p-2 rounded-lg text-gray-500 hover:text-white hover:bg-dark-800 transition-colors"
        title="Go back"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      <div className="absolute top-8 right-8 flex items-center gap-3 text-xs text-gray-500">
        {user?.email && <span>Signed in as {user.email}</span>}
        <button
          onClick={logout}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-dark-800 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Get started with UM-DragonEye</h1>
      <p className="text-sm text-gray-500 mb-8">
        You're not in any organisation yet. Create one or join via an invitation.
      </p>

      <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Create org */}
        <div className="bg-dark-800 border border-orange-600/30 rounded-xl p-6 flex flex-col gap-4">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="w-12 h-12 bg-orange-500/10 border border-orange-500/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-orange-400" />
            </div>
            <h2 className="text-base font-bold text-white">Create Organisation</h2>
            <p className="text-xs text-gray-500 leading-relaxed">
              Provision a new isolated workspace. You'll be assigned Org Admin automatically.
            </p>
          </div>

          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            <div>
              <label htmlFor="org-name" className="block text-xs text-gray-500 mb-1.5">Organisation name *</label>
              <input
                id="org-name"
                required
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="e.g. Acme Sdn Bhd"
                className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors"
              />
            </div>
            {createError && <p className="text-xs text-red-400">{createError}</p>}
            <button
              type="submit"
              disabled={creating}
              className="w-full py-2.5 bg-orange-500 text-gray-950 rounded-lg text-sm font-bold hover:bg-orange-400 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create Tenant Organisation'}
            </button>
          </form>
        </div>

        {/* Join via invite */}
        <div className="bg-dark-800 border border-violet-500/30 rounded-xl p-6 flex flex-col gap-4">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="w-12 h-12 bg-violet-400/10 border border-violet-400/20 rounded-xl flex items-center justify-center">
              <Link2 className="w-6 h-6 text-violet-400" />
            </div>
            <h2 className="text-base font-bold text-white">Join via Invitation</h2>
            <p className="text-xs text-gray-500 leading-relaxed">
              Have an invite link? Paste it below to join an existing organisation.
            </p>
          </div>

          <form onSubmit={handleJoin} className="flex flex-col gap-3">
            <div>
              <label htmlFor="invite-link" className="block text-xs text-gray-500 mb-1.5">Invitation link or token *</label>
              <input
                id="invite-link"
                required
                value={inviteInput}
                onChange={e => { setInviteInput(e.target.value); setJoinError(null); }}
                placeholder="https://…/invite?token=…"
                className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors"
              />
            </div>
            {joinError && <p className="text-xs text-red-400">{joinError}</p>}
            {joinSuccess && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" />
                Invitation accepted — refreshing…
              </div>
            )}
            <button
              type="submit"
              disabled={joining || joinSuccess}
              className="w-full py-2.5 bg-violet-500 text-white rounded-lg text-sm font-bold hover:bg-violet-400 transition-colors disabled:opacity-50"
            >
              {joining ? 'Joining…' : joinSuccess ? 'Joined!' : 'Join Organisation'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NoOrgState;
